/**
 * Nmbrs Leave Request Form (HR "namens een medewerker aanvragen")
 *
 * Form component for HR to submit a leave request on behalf of an
 * employee. Originally Story 3.3 (Epic 3, Verlofbeheer track) called the
 * legacy direct-insert `POST /nmbrs/leave/request` route directly.
 *
 * MIGRATED (Spoor 2, Verlof MVP, Epic 3, Story 3.3, AD-2): that legacy
 * route is removed — this form now submits through the workflow endpoint
 * (`POST /api/v1/leave-requests`) with the HR-only `employeeId` override
 * (Story 3.3, AC 5), which auto-approves and books through the exact same
 * `leaveRequestService.bookApprovedLeaveInNmbrs` code path the normal
 * decide-flow uses — one Nmbrs-booking code path, not two.
 *
 * Design system:
 * - Navy #2F5A7A: primary buttons, headers
 * - Teal #54949E: secondary/info elements
 * - Green #B7D945: success states
 * - Red #D64545: errors
 * - Border #E2E8F0: form borders
 * - Text #1E293B: primary text
 */

import { useState, useEffect } from 'react';
import { useLinkedEmployees } from '../hooks/useNmbrs';
import { useLeaveBalances } from '../hooks/useNmbrsLeave';
import { useSubmitLeaveRequest } from '../../verlof/hooks/useSubmitLeaveRequest';

// ============================================
// Constants
// ============================================

/** Frontend copy of LEAVE_TYPE_MAP (matches backend nmbrsLeaveTypeMap.ts). */
const LEAVE_TYPE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Wettelijk verlof' },
  { value: 2, label: 'Bovenwettelijk verlof' },
  { value: 3, label: 'Kort verzuim + Bijz.verlof' },
  { value: 4, label: 'TvT uren' },
];

const HOURS_PER_BUSINESS_DAY = 8;

// ============================================
// Helpers
// ============================================

/**
 * Parse an ISO date string (YYYY-MM-DD) as a local date.
 * Using `new Date("2026-04-01")` parses as UTC midnight, which can shift
 * to the previous day in negative-offset timezones. This avoids that.
 */
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

/** Count business days between two ISO date strings (inclusive). */
function countBusinessDays(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (end < start) return 0;

  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

/** Format date string for display. */
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ============================================
// Sub-components
// ============================================

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-[#1E293B]">
        {label}
        {required && <span className="text-[#D64545] ml-1">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-[#D64545]">{error}</p>}
    </div>
  );
}

// ============================================
// Balance Preview
// ============================================

/**
 * Geëxporteerd om direct te kunnen toetsen (zelfde motief als
 * `calculateLeaveUsage` in de backend): dit is de plek waar het oordeel "kan
 * deze aanvraag?" wordt gevormd, en die drie uitkomsten wil je vastleggen
 * zonder eerst het hele formulier te hoeven invullen.
 */
export function BalancePreview({
  employeeId,
  leaveType,
  requestedHours,
}: {
  employeeId: number;
  leaveType: number;
  requestedHours: number;
}) {
  const { data, isLoading } = useLeaveBalances(employeeId);
  const balances = data?.data ?? [];
  const label = LEAVE_TYPE_OPTIONS.find((o) => o.value === leaveType)?.label ?? '';
  const balance = balances.find((b) => b.type === label);

  if (!employeeId || !leaveType) return null;
  if (isLoading) {
    return (
      <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-[#E2E8F0]">
        <div className="h-4 bg-gray-200 rounded w-48 animate-pulse" />
      </div>
    );
  }
  if (!balance) return null;

  // Onbekend saldo (Nmbrs-call mislukt) → wel melden dát het onbekend is, maar
  // nooit een overschrijdingswaarschuwing verzinnen. Met `remaining` als 0
  // gelezen zou élke aanvraag hier "overschrijdt je saldo" krijgen.
  if (balance.remaining === null) {
    return (
      <div className="mt-3 p-3 rounded-lg border border-[#E2E8F0] bg-gray-50 text-sm text-gray-600">
        Huidig saldo {label} kon niet bij Nmbrs worden opgehaald. Je kunt gewoon aanvragen; je
        leidinggevende ziet het saldo bij de goedkeuring.
      </div>
    );
  }

  // Drie uitkomsten, drie tonen. Het middelste geval is precies waar dit om
  // begonnen is: in februari past een zomervakantie niet in je opbouw maar wél
  // in je jaarruimte. Dat is GEEN waarschuwing — de oude versie remde daar af
  // en stuurde mensen weg bij een aanvraag die gewoon mocht.
  const pastInOpbouw = requestedHours <= balance.remaining;
  const pastInJaarruimte = balance.yearRemaining !== null && requestedHours <= balance.yearRemaining;

  if (!pastInOpbouw && pastInJaarruimte) {
    return (
      <div className="mt-3 p-3 rounded-lg border border-[#2F5A7A]/30 bg-[#2F5A7A]/5 text-sm text-[#1E293B]">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-[#2F5A7A] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            Dit valt buiten wat je nu hebt opgebouwd ({balance.remaining} uur), maar past binnen je
            jaarruimte van <strong>{balance.yearRemaining} uur</strong>. Vooruit aanvragen mag — je
            bouwt de rest van het jaar verder op.
          </span>
        </div>
      </div>
    );
  }

  // Overschrijdt ook de jaarruimte, of we kennen die niet: dan valt het terug
  // op de opgebouwde stand als maatstaf. Waarschuwen, nooit blokkeren — de
  // leidinggevende beslist.
  const willExceed = !pastInOpbouw;

  return (
    <div
      className={`mt-3 p-3 rounded-lg border text-sm ${
        willExceed
          ? 'bg-orange-50 border-orange-200 text-orange-800'
          : 'bg-[#f0f8f0] border-[#B7D945]/40 text-[#1E293B]'
      }`}
    >
      <div className="flex items-center gap-2">
        {willExceed ? (
          <svg className="w-4 h-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-[#2F5A7A] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <span>
          {willExceed ? (
            <>
              Aanvraag van <strong>{requestedHours} uur</strong>
              {balance.yearRemaining !== null ? (
                <>
                  {' '}is meer dan je dit jaar nog kunt plannen (<strong>{balance.yearRemaining} uur</strong>).
                  Je leidinggevende kan er alsnog mee akkoord gaan.
                </>
              ) : (
                <> overschrijdt je opgebouwde saldo van <strong>{balance.remaining} uur</strong> (waarschuwing).</>
              )}
            </>
          ) : (
            <>
              Past binnen je opgebouwde saldo {label}: <strong>{balance.remaining} uur</strong> beschikbaar
            </>
          )}
        </span>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function NmbrsLeaveRequestForm() {
  const { data: linksData, isLoading: linksLoading } = useLinkedEmployees();
  const submitMutation = useSubmitLeaveRequest();

  const linkedEmployees = linksData?.data ?? [];

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>(0);
  const [leaveType, setLeaveType] = useState<number>(1);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [hours, setHours] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Auto-calculate hours from business days
  useEffect(() => {
    if (startDate && endDate && endDate >= startDate) {
      const days = countBusinessDays(startDate, endDate);
      setHours(String(days * HOURS_PER_BUSINESS_DAY));
    }
  }, [startDate, endDate]);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!selectedEmployeeId) newErrors.employeeId = 'Selecteer een medewerker';
    if (!leaveType) newErrors.leaveType = 'Selecteer een verloftype';
    if (!startDate) newErrors.startDate = 'Startdatum is verplicht';
    if (!endDate) newErrors.endDate = 'Einddatum is verplicht';
    if (startDate && endDate && endDate < startDate) {
      newErrors.endDate = 'Einddatum moet op of na startdatum liggen';
    }
    const hoursNum = parseFloat(hours);
    if (!hours || isNaN(hoursNum) || hoursNum <= 0) {
      newErrors.hours = 'Uren moeten groter dan 0 zijn';
    }
    if (description.length > 500) {
      newErrors.description = 'Omschrijving mag maximaal 500 tekens bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    if (!validate()) return;

    try {
      const result = await submitMutation.mutateAsync({
        employeeId: selectedEmployeeId,
        leaveType,
        startDate,
        endDate,
        // This form has no dagdeel-selector (manual hours entry, business-
        // day-based) — always hele_dag on both edges, matching its
        // existing "auto-calculate from business days" assumption.
        startPart: 'hele_dag',
        endPart: 'hele_dag',
        hours: parseFloat(hours),
        description: description.trim() || undefined,
      });

      const typeName = LEAVE_TYPE_OPTIONS.find((o) => o.value === leaveType)?.label ?? '';
      const overlapNote = result.data.warning === 'OVERLAP' ? ' Let op: overlapt met een bestaande aanvraag van deze medewerker.' : '';
      setSuccessMessage(
        `Verlofaanvraag ingediend en automatisch goedgekeurd: ${typeName} van ${formatDate(startDate)} t/m ${formatDate(endDate)} (${hours} uur). De Nmbrs-boeking is gestart — zie de sync-status in Goedkeuringen.${overlapNote}`
      );

      // Reset form
      setSelectedEmployeeId(0);
      setLeaveType(1);
      setStartDate('');
      setEndDate('');
      setHours('');
      setDescription('');
      setErrors({});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Onbekende fout';
      setErrors({ submit: message });
    }
  }

  const hoursNum = parseFloat(hours);

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E2E8F0]">
        <h3 className="text-base font-semibold text-[#2F5A7A]">Verlofaanvraag indienen</h3>
        <p className="text-xs text-gray-500 mt-0.5">Dien een verlofaanvraag in en synchroniseer met Nmbrs</p>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Employee selector */}
        <FormField label="Medewerker" required error={errors.employeeId}>
          <select
            value={selectedEmployeeId}
            onChange={(e) => setSelectedEmployeeId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E] disabled:opacity-50"
            disabled={linksLoading}
          >
            <option value={0}>-- Selecteer medewerker --</option>
            {linkedEmployees.map((link) => (
              <option key={link.employeeId} value={link.employeeId}>
                {link.employeeName}
              </option>
            ))}
          </select>
          {linksLoading && (
            <p className="text-xs text-gray-400 mt-1">Medewerkers laden...</p>
          )}
          {!linksLoading && linkedEmployees.length === 0 && (
            <p className="text-xs text-[#54949E] mt-1">
              Geen gekoppelde medewerkers gevonden. Koppel eerst medewerkers aan Nmbrs.
            </p>
          )}
        </FormField>

        {/* Leave type */}
        <FormField label="Verloftype" required error={errors.leaveType}>
          <select
            value={leaveType}
            onChange={(e) => setLeaveType(Number(e.target.value))}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
          >
            {LEAVE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FormField>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Startdatum" required error={errors.startDate}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
            />
          </FormField>

          <FormField label="Einddatum" required error={errors.endDate}>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
            />
          </FormField>
        </div>

        {/* Hours */}
        <FormField label="Aantal uren" required error={errors.hours}>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              min="0.5"
              step="0.5"
              placeholder="0"
              className="w-32 px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
            />
            {startDate && endDate && endDate >= startDate && (
              <span className="text-xs text-gray-500">
                Auto-berekend: {countBusinessDays(startDate, endDate)} werkdagen × {HOURS_PER_BUSINESS_DAY} uur
              </span>
            )}
          </div>
        </FormField>

        {/* Balance preview */}
        {selectedEmployeeId > 0 && leaveType > 0 && (
          <BalancePreview
            employeeId={selectedEmployeeId}
            leaveType={leaveType}
            requestedHours={isNaN(hoursNum) ? 0 : hoursNum}
          />
        )}

        {/* Description */}
        <FormField label="Omschrijving" error={errors.description}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Optioneel: reden voor verlof (max. 500 tekens)"
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E] resize-none"
          />
          <p className="text-xs text-gray-400 text-right">{description.length}/500</p>
        </FormField>

        {/* Submit error */}
        {errors.submit && (
          <div className="p-3 rounded-lg bg-red-50 border border-[#D64545]/20">
            <p className="text-sm text-[#D64545]">{errors.submit}</p>
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="p-3 rounded-lg bg-[#f0f8f0] border border-[#B7D945]/40">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-[#2F5A7A] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-[#1E293B]">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={submitMutation.isPending || linkedEmployees.length === 0}
            className="px-5 py-2 text-sm font-medium text-white bg-[#2F5A7A] rounded-lg hover:bg-[#254a65] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {submitMutation.isPending ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Indienen...
              </>
            ) : (
              'Verlof indienen'
            )}
          </button>
          {submitMutation.isPending && (
            <span className="text-xs text-gray-500">Aanvraag wordt gesynchroniseerd met Nmbrs...</span>
          )}
        </div>
      </form>
    </div>
  );
}
